import React from 'react';
import { useTranslation } from 'react-i18next';

import { HeaderText, SectionContainer, SectionSubtitle } from '../../../components/Modals';
import { Flex } from '../../../components/ui/Flex';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '../../../components/ui/Form';
import { SettingsModalInput } from '../../settings/SettingsModals';
import { DATA_CENTERS, LOCK_PROVIDERS, ORGANIZATION_PROVIDERS } from '../../../utils/mappings';
import { Select, SelectItem } from '../../../components/ui/Select';
import ImageUploadWithPreviewV3 from '../../../components/ImageUploadWithPreviewV3';
import { LockProviders } from '../../../utils/lockInfo';


const OrgInfoFormInputs = ({ form, lockProvider, existingLogoUrl, isEdit }) => {
  const { t } = useTranslation();
  return (
    <SectionContainer>
      <HeaderText alt>{t('Organization Information')}</HeaderText>
      <SectionSubtitle>
        {t('Provide the basic info and system preferences for this organization.')}
      </SectionSubtitle>
      <Flex direction="column" gap="16px">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Org Name')}</FormLabel>
              <FormControl row>
                <SettingsModalInput
                  type="text"
                  placeholder={t('Enter Organization Name')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Email')}</FormLabel>
              <FormControl row>
                <SettingsModalInput type="text" placeholder={'name@organization.com'} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="contact"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Contact Person')}</FormLabel>
              <FormControl row>
                <SettingsModalInput type="text" placeholder={t('Full Name')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Flex gap="24px">
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Data Center')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Select a region')} {...field}>
                    {DATA_CENTERS.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lock_provider"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Lock Brand')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Choose lock provider')} {...field}>
                    {LOCK_PROVIDERS.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Flex>

        {(lockProvider === LockProviders.YALE || lockProvider === LockProviders.ICT_YALE || lockProvider === LockProviders.BRIVO_YALE) && !isEdit && (
          <>
            <FormField
              control={form.control}
              name="provider_access_token"
              render={({ field }) => (
                <FormItem alt>
                  <FormLabel v3>{t('Yale Access Token')}</FormLabel>
                  <FormControl row>
                    <SettingsModalInput
                      type="text"
                      placeholder={t('Yale Access Token')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="provider_refresh_token"
              render={({ field }) => (
                <FormItem alt>
                  <FormLabel v3>{t('Yale Refresh Token')}</FormLabel>
                  <FormControl row>
                    <SettingsModalInput
                      type="text"
                      placeholder={t('Yale Refresh Token')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        {(lockProvider === LockProviders.YALE || lockProvider === LockProviders.ICT_YALE || lockProvider === LockProviders.BRIVO_YALE) && (
          <FormField
            control={form.control}
            name="house_id"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('House ID')}</FormLabel>
                <FormControl row>
                  <SettingsModalInput
                    type="text"
                    placeholder={t('House ID')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="resident_provider"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Resident Provider')}</FormLabel>
              <FormControl>
                <Select placeholder={t('Please select')} {...field}>
                  {ORGANIZATION_PROVIDERS.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.value}
                    </SelectItem>
                  ))}
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Flex gap="24px">
          <FormField
            control={form.control}
            name="property_provider"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Property Provider')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Please select')} {...field}>
                    {ORGANIZATION_PROVIDERS.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="service_provider"
            render={({ field }) => (
              <FormItem alt>
                <FormLabel v3>{t('Service Provider')}</FormLabel>
                <FormControl>
                  <Select placeholder={t('Please select')} {...field}>
                    {ORGANIZATION_PROVIDERS.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.value}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Flex>

        <ImageUploadWithPreviewV3
          form={form} 
          existingImageUrl={existingLogoUrl} 
          fieldName="logo"
          instructionText={t('Drag and drop your organization avatar here.')}
        />
      </Flex>
    </SectionContainer>
  );
};

export default OrgInfoFormInputs;
